/**
 * Central cache-invalidation bus.
 *
 * Read-side caches (stek list, worktree, entity detail, …) go stale when a
 * mutation lands but nothing tells them to refresh. Historically each store
 * subscribed to the sync store with its own hard-coded *attribute* whitelist
 * (e.g. only "name"/"description"), so any field outside that list — logos,
 * relationships, … — was silently missed.
 *
 * This bus inverts that: a cache registers once, keyed by the *entity* it owns
 * (via a predicate over the change), and is refreshed whenever ANY change to
 * that entity lands. Forgetting a field can now only cost a harmless extra
 * refresh, never staleness. A single sync producing several changes refreshes
 * each cache at most once (deduped per batch).
 *
 * The only producer is the sync store, which calls `notifyEntityChanged` with
 * the successfully-synced identities (see its `$onAction("sync")` wiring).
 */

/** Structurally compatible with the sync store's `SyncIdentity`. */
export type EntityChange = {
    organizationId?: string;
    entityType: string;
    entityId: string;
    attribute?: string;
    /**
     * How the change reached this tab. A local sync already has the newest
     * entity-detail state in memory; detail editors must not refetch it and
     * replace their live editor state. Other read-side caches (worktree,
     * lists, thread changes, ...) still consume the same invalidation.
     */
    origin?: "local-sync";
};

type Predicate = (change: EntityChange) => boolean;
type Handler = () => void;

const registrations = new Set<{ match: Predicate; run: Handler }>();

/**
 * Register a cache invalidator. `run` is invoked when a notified change
 * satisfies `match`. Returns a disposer that removes the registration.
 */
export function onEntityChange(match: Predicate, run: Handler): () => void {
    const registration = { match, run };
    registrations.add(registration);
    return () => {
        registrations.delete(registration);
    };
}

/**
 * Notify the bus that a batch of entity changes has landed. Every registration
 * whose predicate matches at least one change runs exactly once.
 */
export function notifyEntityChanged(changes: readonly EntityChange[]): void {
    const toRun = new Set<Handler>();
    for (const change of changes) {
        for (const registration of registrations) {
            if (registration.match(change)) {
                toRun.add(registration.run);
            }
        }
    }
    toRun.forEach((run) => run());
}

/** Test-only: drop all registrations so specs start from a clean bus. */
export function __resetCacheInvalidation(): void {
    registrations.clear();
}
